import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_ORIGIN } from '../../config';
import type { AvatarView } from '../profiles/api';
import type { RelationshipWithPeer } from '../relationships/api';

/**
 * Invite links (2026-10-08): a link a member shares anywhere (WhatsApp, a text)
 * so someone can come and write to them. The server closes a link after a week
 * or a handful of new people; opening one is the same as writing to the member
 * by @handle.
 */
export interface InviteLinkView {
  url: string;
  expiresAt: string;
  usesLeft: number;
}

export interface InviteLinkPreview {
  handle: string;
  displayName: string | null;
  avatar: AvatarView | null;
  /** A lasting link that has started all the new pairs it can today. */
  fullToday: boolean;
  /** A gift link: the stamp book waiting for whoever opens it. */
  gift: { collectionName: string } | null;
}

/** The member's lasting link (slice 4): for the QR code, story card and bio. */
export interface LastingLinkView {
  url: string;
  leftToday: number;
  dailyCap: number;
}

export const inviteLinksApi = createApi({
  reducerPath: 'inviteLinksApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_ORIGIN}/api/invite-links`, credentials: 'include' }),
  tagTypes: ['InviteLink', 'LastingLink'],
  endpoints: (b) => ({
    /** My open link — the server makes one on first ask. */
    myInviteLink: b.query<InviteLinkView, void>({
      query: () => '/mine',
      providesTags: ['InviteLink'],
    }),
    renewInviteLink: b.mutation<InviteLinkView, void>({
      query: () => ({ url: '/mine/renew', method: 'POST' }),
      invalidatesTags: ['InviteLink'],
    }),
    lastingLink: b.query<LastingLinkView, void>({
      query: () => '/lasting',
      providesTags: ['LastingLink'],
    }),
    resetLastingLink: b.mutation<LastingLinkView, void>({
      query: () => ({ url: '/lasting/reset', method: 'POST' }),
      invalidatesTags: ['LastingLink'],
    }),
    /** Public: who a link leads to. */
    inviteLinkPreview: b.query<InviteLinkPreview, string>({
      query: (token) => `/${encodeURIComponent(token)}`,
    }),
    /** `via` is the channel tag the link arrived with (kept beside the pending token). */
    openInviteLink: b.mutation<RelationshipWithPeer, { token: string; via: string | null }>({
      query: ({ token, via }) => ({ url: `/${encodeURIComponent(token)}/open`, method: 'POST', body: { via } }),
    }),
  }),
});

export const {
  useMyInviteLinkQuery,
  useRenewInviteLinkMutation,
  useInviteLinkPreviewQuery,
  useOpenInviteLinkMutation,
  useLastingLinkQuery,
  useResetLastingLinkMutation,
} = inviteLinksApi;
